//
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMount } from 'react-use';

import Box from '@mui/material/Box/Box';
import Typography from '@mui/material/Typography';

import { authSelectors } from 'app-components/flux/auth';
import { datasetsActions, datasetsSelectors, datasetsUtils } from 'app-components/flux/datasets';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { sharesActions, sharesSelectors } from 'app-components/flux/shares';
import { subscriptionsActions, subscriptionsSelectors } from 'app-components/flux/subscriptions';
import { useFetchOnce } from 'app-components/hooks/utils';
import { getEnvironmentConfig, platform, tracker } from 'app-components/utils/core';

import GuidedTooltip from 'components/GuidedTooltip';
import QButton from 'components/QButton';
import SettingsHeader from 'containers/Settings/SharedComponents/SettingsHeader';

import DatasetCard from './DatasetCard';
import UpsertDatasetDialog from './UpsertDatasetDialog';

function Sharing() {
  const navigate = useNavigate();

  const [isChromebook, setIsChromebook] = useState(false);
  const [showAddDatasetDialog, setShowAddDatasetDialog] = useState(false);
  const [addSubscriptionAnchorEl, setAddSubscriptionAnchorEl] = useState();

  useFetchOnce(datasetsActions.getDatasets, datasetsSelectors, true);
  useFetchOnce(sharesActions.getShares, sharesSelectors, true);
  useFetchOnce(subscriptionsActions.getSubscriptions, subscriptionsSelectors, true);

  const playBillingFeatureFlag = useSelector(featureFlagsSelectors.getFeatureFlags)?.get('playBilling');
  const isPlayBillingEnabled = playBillingFeatureFlag && isChromebook;

  const activeSubscription = useSelector(subscriptionsSelectors.getActiveSubscription);
  const authSession = useSelector(authSelectors.getAuthSession);
  const currentDatasetId = useSelector(authSelectors.getDatasetId);
  const datasets = useSelector(datasetsSelectors.getDatasets);
  const maxDatasets = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'maxDatasets'));
  const shares = useSelector(sharesSelectors.getSharesById);

  useEffect(() => {
    tracker.track(tracker.events.viewSpaces);
  }, []);

  useMount(async () => {
    const chromebook = await platform.isChromebook();
    setIsChromebook(chromebook);
  });

  const datasetsGroupByOwned = datasets
    .toList()
    .groupBy((dataset) => !dataset.shared)
    .map((group) =>
      group.sort((dataset1, dataset2) => {
        if (dataset1.id === currentDatasetId) return -1;
        if (dataset2.id === currentDatasetId) return 1;
        return dataset1.name.localeCompare(dataset2.name, undefined, { numeric: true, sensitivity: 'base' });
      }),
    );
  const sharesByDatasetId = shares.reduce((map, share) => {
    let sharesForDatasetId = map.get(share.datasetId);
    if (!sharesForDatasetId) {
      map.set(share.datasetId, (sharesForDatasetId = []));
    }
    sharesForDatasetId.push(share);
    return map;
  }, new Map());

  const numOwnedDatasets = datasetsGroupByOwned.get(true) ? datasetsGroupByOwned.get(true).size : 0;
  const enableAddDatasetButton = (numOwnedDatasets < maxDatasets || !maxDatasets) && activeSubscription;

  const handleOnAddDataset = (event) => {
    if (maxDatasets) {
      setShowAddDatasetDialog(true);
    } else {
      setAddSubscriptionAnchorEl(event.currentTarget);
    }
  };

  const handleAddSubcription = () => {
    if (isPlayBillingEnabled) {
      navigate('/pick-your-plan');
    } else {
      window.location.href = `${getEnvironmentConfig().pick_your_plan_url}?bearer=${authSession.accessToken}`;
    }
  };

  return (
    <>
      <SettingsHeader
        title='Spaces & sharing'
        primaryItem={
          <QButton disabled={!enableAddDatasetButton} id='add-dataset' onClick={handleOnAddDataset} variant='add'>
            Space
          </QButton>
        }
      />
      {datasetsGroupByOwned.get(true) && (
        <>
          <Typography mt={3} mx={0} variant='titleMedium'>
            Your financial {datasetsGroupByOwned.get(true).size > 1 ? 'spaces' : 'space'}
          </Typography>
          <Box display='flex' flexWrap='wrap'>
            {datasetsGroupByOwned.get(true).map((dataset, index) => (
              <DatasetCard
                id={`dataset-card-${index}`}
                dataset={dataset}
                key={dataset.id}
                shares={sharesByDatasetId.get(dataset.id)}
                disabled={!datasetsUtils.hasActiveEntitlement(dataset)}
              />
            ))}
          </Box>
        </>
      )}
      {datasetsGroupByOwned.get(false) && (
        <>
          <Typography mt={3} mx={0} variant='titleMedium'>
            Shared with you
          </Typography>
          <Box display='flex' flexWrap='wrap'>
            {datasetsGroupByOwned.get(false).map((dataset) => (
              <DatasetCard
                dataset={dataset}
                key={dataset.id}
                shares={sharesByDatasetId.get(dataset.id)}
                disabled={!datasetsUtils.hasActiveEntitlement(dataset)}
              />
            ))}
          </Box>
        </>
      )}
      {showAddDatasetDialog && <UpsertDatasetDialog onClose={() => setShowAddDatasetDialog(false)} />}
      <GuidedTooltip
        anchorEl={addSubscriptionAnchorEl}
        continueLabel='Add subscription'
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 12],
            },
          },
        ]}
        open={!!addSubscriptionAnchorEl}
        onCancel={() => {
          setAddSubscriptionAnchorEl(null);
        }}
        onClickAway={() => {
          setAddSubscriptionAnchorEl(null);
        }}
        onContinue={handleAddSubcription}
        placement='bottom-end'
        title='You do not have a subscription'
        body='To create a space of your own, you need to be a subscriber.'
      />
    </>
  );
}

export default Sharing;
